import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUserAuth, formatApiErrorDetail } from "../lib/UserAuthContext";
import { LOGO_URL } from "../lib/site";

export default function Login() {
  const { login } = useUserAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      const dest = loc.state?.from && loc.state.from !== "/login" ? loc.state.from : "/dashboard";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-8">
          <img src={LOGO_URL} alt="CashPark" className="h-14 w-14" />
          <span className="font-display font-bold text-xl text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Inc</span></span>
        </Link>
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm" data-testid="login-form">
          <h1 className="font-display text-2xl font-bold text-[#0B192C]">Sign in</h1>
          <p className="text-sm text-slate-500 mt-1">Access your CashPark dashboard.</p>

          <div className="mt-6">
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="pl-9 h-11 rounded-xl" />
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="pl-9 pr-10 h-11 rounded-xl" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit" className="mt-6 w-full h-11 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <p className="mt-5 text-sm text-slate-500 text-center">
            Don't have an account? <Link to="/register" className="text-[#FF6B00] font-semibold" data-testid="login-register-link">Sign up</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
